// FIRM POLICY QUESTIONNAIRE (v1_814). One structured, signed statement of a
// tenant's AML policy, in 13 sections (section 6 removed in v1_836), per the approved plan
// (BRIEF-v1_813-plan.md). Pure: no I/O. Shared by the server route
// (api/policy-questionnaire.js), the MLRO page and the harnesses.
//
//  - FIELDS: the 13 fields and what engine key each maps to. Every field is
//    enforced (v1_836: an answer the platform cannot act on is not asked); the
//    only label left is "Fixed by the platform" on section 8, a statement.
//  - generateAnswers(): fills each field from, in order, the firm's current
//    configuration (source mlro), the distilled manual with its clause (source
//    manual), else the CompliantID default (source default). It invents nothing.
//  - validateAnswer(): the engine's own limits, with the reason for a refusal.
//  - buildFirmPolicyConfig(): signed answers into the EXISTING engine keys only.
//    No new decision logic: every value goes where the engine already reads it,
//    within the limits it already applies.
//
// No hyphens or dashes in any string here: the MLRO, the platform owner and the
// Audit Record read them.

import {
  QUESTIONNAIRE_ENTITY_TYPES, QUESTIONNAIRE_ENTITY_LABELS, STARTER_DOCS,
  floorFromQuestionnaireDocs, unenforcedQuestionnaireTypes, QUESTIONNAIRE_TO_BOARD_TYPES, questionnaireTypeForBoardType,
  SPECIFIC_DOC_TYPES, specificDocsOf, adminSpecificByType, adminSpecificDocs, floorBoardTypeFor, boardTypeLabel,
} from './policy-starter-docs.js';
import { normalizeEntityTypeKey, normalizeRequiredDocsByEntityType } from './firm-policy.js';
import { DEFAULT_OWNERSHIP_THRESHOLD, thresholdStatement } from './ownership-threshold.js';
import { ENFORCED_CLIENT_TYPES, NEXT_CLIENT_TYPES, CLIENT_TYPE_LABELS, EDD_FLOOR_CLIENT_TYPES } from './client-types.js';
import { policyOfficersOf } from './policy-officers.js';

// Mirrors of api/_risk-methodology.js (CID-RM-1.4). src cannot import from api
// (the client build refuses paths outside src), so these are copied and the
// questionnaire harness asserts they equal the methodology's own values.
export const CATEGORIES = ['customer', 'sector', 'geography', 'product', 'channel'];
export const WEIGHTS = Object.freeze({ customer: 25, sector: 25, geography: 25, product: 15, channel: 10 });
export const BANDS = Object.freeze({ lowMax: 1.40, highMin: 2.31 });
export const TIER_REQUIREMENTS = Object.freeze({
  Low: { reviewYears: 3, sowRequired: false, seniorApproval: false, adversarialReview: false,
    summary: 'Standard CDD. Simplified measures only where the regime and the firm policy permit and the file evidences the basis. Review every three years.' },
  Medium: { reviewYears: 2, sowRequired: false, seniorApproval: false, adversarialReview: false,
    summary: 'Standard CDD with source of funds documented and corroborated. Review every two years.' },
  High: { reviewYears: 1, sowRequired: true, seniorApproval: true, adversarialReview: true,
    summary: 'Enhanced due diligence: additional verification of identity and ownership, source of wealth and source of funds evidenced, senior management or MLRO approval before establishment, enhanced ongoing monitoring, adversarial review. Review annually.' }
});

// v1_836: PQ 2 (section 6 removed; sections 1, 3, 4, 5, 10, 11, 14 enforced).
// A firm with a signed PQ 1 keeps it as its policy basis until it re signs.
export const QUESTIONNAIRE_VERSION = 'PQ-2';
const versionNumber = (v) => { const m = String(v || '').match(/(\d+)/); return m ? Number(m[1]) : 1; };
// The version a questionnaire row was generated at (a row without one is PQ 1).
export const questionnaireVersionOf = (row) => versionNumber(row && row.generated_from && row.generated_from.questionnaireVersion);
// The notice for a firm whose signed version predates this one, else null.
export function newVersionNotice(signed) {
  if (!signed || questionnaireVersionOf(signed) >= versionNumber(QUESTIONNAIRE_VERSION)) return null;
  return `A new version of the questionnaire is available (version ${versionNumber(QUESTIONNAIRE_VERSION)}). Your signed version ${questionnaireVersionOf(signed)} stays your policy basis until you re sign; re signing applies the new sections, and a case takes them at its next Reprocess.`;
}

export const CLIENT_TYPES = Object.freeze([
  { key: 'retail', label: 'Retail clients' },
  { key: 'unincorporated', label: 'Unincorporated businesses' },
  { key: 'trusts_charities', label: 'Trusts and charities' },
  { key: 'ceps', label: 'Complex or opaque ownership structures' },
  { key: 'pep_connected', label: 'Clients connected to a politically exposed person', fixed: 'edd' },
  { key: 'crypto_businesses', label: 'Crypto businesses' },
]);
// v1_831: which section 1 types an Excluded answer acts on now, and which from
// the next change (v1_832, the entity profile mapping). Shown under section 1.
export const CLIENT_TYPE_ENFORCEMENT = Object.freeze({
  now: ENFORCED_CLIENT_TYPES.map(k => CLIENT_TYPE_LABELS[k]),
  next: NEXT_CLIENT_TYPES.map(k => CLIENT_TYPE_LABELS[k]),
});
export function clientTypeEnforcementNote() {
  const lower = (l) => l.charAt(0).toLowerCase() + l.slice(1);
  const list = (a) => a.length === 1 ? lower(a[0]) : a.slice(0, -1).map(lower).join(', ') + ' and ' + lower(a[a.length - 1]);
  const now = `Excluded is enforced for ${CLIENT_TYPE_ENFORCEMENT.next.length ? '' : 'every client type: '}${list(CLIENT_TYPE_ENFORCEMENT.now)}. Such a case opens a Decline gate, rates High and cannot be adjusted below High.`;
  const next = CLIENT_TYPE_ENFORCEMENT.next.length ? ` ${list(CLIENT_TYPE_ENFORCEMENT.next).replace(/^./, c => c.toUpperCase())} are recorded now and enforced from the next change.` : ' Retail clients, unincorporated businesses, and trusts and charities are read from the client\'s legal form (set at invitation, read from the documents, or set by the MLRO).';
  // v1_848: what EDD does, type by type.
  const edd = ' EDD rates such a case High and it cannot be adjusted below High (retail clients, unincorporated businesses, trusts and charities, complex or opaque ownership); for crypto businesses EDD keeps the sector High; a politically exposed person connection is EDD by law.';
  return now + next + edd;
}

// Client type treatment "EDD" maps to the firm's sector override (sectorRisk =
// High) where a sector key exists: crypto businesses, VASP. v1_848: the four
// types with no sector key (retail, unincorporated, trusts and charities,
// complex ownership) go to risk_config.eddClientTypes, where the engine rates
// the case High with a High floor (the clientTypeEdd override).
const CLIENT_TYPE_SECTOR = Object.freeze({ crypto_businesses: ['VASP'] });

const TIERS = ['Low', 'Medium', 'High'];

// source: manual | default | mlro | platform_owner_pdf
// enforced: 'yes' | 'fixed' (v1_836: nothing asked that the platform does not act on)
export const FIELDS = Object.freeze([
  // v1_831: Excluded opens the clientTypeExcluded gate (Decline) for the three
  // types detected on a case today; the other three follow with v1_832.
  // v1_832: all six Excluded answers act on a case (three from the legal form).
  { id: 'clientTypes', n: 1, title: 'Client types', enforced: 'yes',
    engineKey: 'risk_config.excludedClientTypes (the clientTypeExcluded gate); EDD: risk_config.sectorRisk for crypto businesses, risk_config.eddClientTypes (the clientTypeEdd override, a High floor) for the four other types' },
  { id: 'jurisdictions', n: 2, title: 'Prohibited and high risk jurisdictions', enforced: 'yes',
    engineKey: 'risk_config.countryPolicy' },
  // v1_830: the main threshold (risk_config.uboThreshold, at or above).
  // v1_832: the per type values too (risk_config.uboThresholdByType), by the
  // legal form group, then the entity type group, then the main value.
  { id: 'uboThreshold', n: 3, title: 'Beneficial ownership threshold', enforced: 'yes',
    engineKey: 'risk_config.uboThreshold and risk_config.uboThresholdByType' },
  // v1_832: partnership, trust and individual lists apply by the legal form.
  { id: 'mandatoryDocs', n: 4, title: 'Mandatory documents by entity type', enforced: 'yes',
    engineKey: 'compliance_config.housePolicy.fields.requiredDocsByEntityType (the house policy floor); partnership, trust and individual by the client\'s legal form' },
  // v1_834w: v1_834 withdrawn. Section 5 asks the bands and weights only; a
  // written list of factors is not collected (the firm's manual is the source),
  // and no firm free text reaches the assessment or rationale prompts.
  { id: 'riskCategorisation', n: 5, title: 'Risk categorisation', enforced: 'yes',
    engineKey: 'risk_config.bands and risk_config.weights' },
  // v1_836: section 6 (EDD and escalation triggers with values) removed. These
  // are transaction rules; onboarding sees no transactions (monitoring's build).
  { id: 'kycRenewal', n: 7, title: 'KYC renewal by risk tier', enforced: 'yes',
    engineKey: 'risk_config.tierRequirements.<tier>.reviewYears' },
  { id: 'idScope', n: 8, title: 'Identity verification scope', enforced: 'fixed',
    engineKey: 'none (directors, beneficial owners and authorised signatories always)',
    notEnforcedWhy: 'A narrower scope would reduce verification, so the platform minimum stays.' },
  // v1_830: the holding threshold is section 3's (one field, not two).
  { id: 'sowInterview', n: 9, title: 'Source of wealth personal interview', enforced: 'yes',
    engineKey: 'risk_config.tierRequirements.<tier>.sowRequired (holders at or above the section 3 threshold)' },
  { id: 'screening', n: 10, title: 'Screening treatment and approval', enforced: 'yes',
    engineKey: 'features.direct_approval (second sign off); compliance_config.policyOfficers.approver (shown at sign off, recorded on the decision)' },
  { id: 'roles', n: 11, title: 'Named MLRO, deputy, second signatory and override approver', enforced: 'yes',
    engineKey: 'firms.second_signoff_name, firms.second_signoff_email; compliance_config.policyOfficers (MLRO, deputy, override approver: recorded on the decision)' },
  // v1_848: the "Crypto in scope" box is removed: it drove nothing. Whether the
  // firm takes crypto businesses is section 1's (Permitted, Excluded or EDD,
  // detected by the VASP entity type); section 12 holds the two switches.
  { id: 'crypto', n: 12, title: 'Wallet screening and the Travel Rule', enforced: 'yes',
    engineKey: 'features.chainalysis, features.travel_rule',
    // v1_824: what ticking wallet screening does, in the product's own terms.
    walletHelp: 'Ticking wallet screening turns on the wallet screening row: when a client indicates crypto, the case shows a wallet screening row. When a wallet address is on file and not yet screened, sign off waits until it is screened or the officer resolves the row with a reason, recorded in the case notes and the Audit Record. With no address on file the row reads "No address on file, screen at first transfer" and does not hold sign off.' },
  { id: 'clientTerms', n: 13, title: 'Client terms', enforced: 'yes',
    engineKey: 'compliance_config.clientTermsOutside' },
  // v1_833: the questions are asked of every client too (compliance_config.firmQuestions).
  { id: 'everyClient', n: 14, title: 'Asked of every client regardless of risk', enforced: 'yes',
    engineKey: 'compliance_config.documentChecklist.common and compliance_config.firmQuestions' },
]);
export const FIELD_BY_ID = Object.freeze(Object.fromEntries(FIELDS.map(f => [f.id, f])));

// THE COMPLIANTID DEFAULTS: the platform's current behaviour, stated plainly,
// so confirming a default changes nothing. Mandatory documents default to the
// starter lists as a PROPOSAL (general AML principles until signed).
export function defaultValue(id) {
  switch (id) {
    case 'clientTypes': return Object.fromEntries(CLIENT_TYPES.map(t => [t.key, t.fixed || 'permitted']));
    case 'jurisdictions': return { prohibited: [], high: [] };
    case 'uboThreshold': return { percent: 25, variesByType: false };
    case 'mandatoryDocs': return Object.fromEntries(QUESTIONNAIRE_ENTITY_TYPES.map(t => [t, [...STARTER_DOCS[t]]]));
    case 'riskCategorisation': return { lowMax: BANDS.lowMax, highMin: BANDS.highMin, weights: { ...WEIGHTS } };
    case 'kycRenewal': return Object.fromEntries(TIERS.map(t => [t, TIER_REQUIREMENTS[t].reviewYears]));
    case 'idScope': return { scope: 'directors_ubos_signatories' };
    case 'sowInterview': return { tiers: Object.fromEntries(TIERS.map(t => [t, !!TIER_REQUIREMENTS[t].sowRequired])) };
    case 'screening': return { pepTreatment: 'high_edd', approver: '', secondSignoff: false };
    case 'roles': return { mlro: '', deputy: '', secondSignatoryName: '', secondSignatoryEmail: '', overrideApprover: '' };
    case 'crypto': return { walletScreening: false, travelRule: false };
    case 'clientTerms': return { where: 'platform' };
    case 'everyClient': return { documents: [], questions: [] };
    default: return null;
  }
}

const str = (v) => (v == null ? '' : String(v)).trim();
const list = (v) => (Array.isArray(v) ? v : (typeof v === 'string' && v.trim() ? v.split(/\n|;/) : [])).map(str).filter(Boolean);
const clip = (s, n) => { const t = str(s); return t.length > n ? t.slice(0, n - 1).trimEnd() + '…' : t; };

// A distilled requiredDocsByEntityType key (free form) to a questionnaire type.
export function questionnaireTypeForKey(key) {
  const s = str(key).toLowerCase();
  if (!s) return null;
  if (/partnership|\bllp\b|\blp\b/.test(s)) return 'partnership';
  if (/trust|foundation|charit/.test(s)) return 'trust';
  if (/individual|natural person|sole trader|private client|person/.test(s)) return 'individual';
  if (/regulated|licensed|authori[sz]ed/.test(s)) return 'regulated_firm';
  if (/holding|spv|special purpose|private (limited )?compan|corporate|company|limited/.test(s)) return 'private_company';
  const board = normalizeEntityTypeKey(key);
  for (const qt of QUESTIONNAIRE_ENTITY_TYPES) if (QUESTIONNAIRE_TO_BOARD_TYPES[qt].includes(board)) return qt;
  return null;
}

// Which field a distiller note (uncertainty or conflict) concerns. Keyword
// routing only; anything unrouted goes to "Points the manual left open".
const NOTE_ROUTES = [
  ['jurisdictions', /jurisdiction|countr|sanction(ed)? (countr|state)|fatf|high risk third/i],
  ['uboThreshold', /beneficial own|\bubo\b|ownership threshold|25\s*(%|percent)|10\s*(%|percent)/i],
  ['mandatoryDocs', /document|certificate|register|evidence|proof of/i],
  ['kycRenewal', /review (period|cycle|frequenc)|renewal|refresh|periodic review/i],
  ['sowInterview', /source of wealth|source of funds|\bsow\b|\bsof\b/i],
  ['crypto', /crypto|virtual asset|wallet|travel rule|blockchain/i],
  ['screening', /\bpep\b|politically exposed|screening|adverse media|senior (management )?approval/i],
  ['roles', /\bmlro\b|deputy|money laundering reporting officer|nominated officer|signator/i],
  ['clientTerms', /terms of business|client terms|terms and conditions|engagement letter/i],
  ['riskCategorisation', /risk (rating|score|categor|matrix|methodolog)|low risk|high risk|medium risk/i],
  ['clientTypes', /retail|unincorporated|charit|client type|prohibited (client|business)|excluded/i],
  ['idScope', /identity verification|verify (the )?identity|signatories/i],
  ['everyClient', /every client|all clients|regardless of risk/i],
];
export function fieldForNote(text) {
  const t = str(text);
  for (const [id, re] of NOTE_ROUTES) if (re.test(t)) return id;
  return null;
}

function entry(value, source, extra = {}) {
  return { value, source, clause: extra.clause || null, quote: extra.quote ? clip(extra.quote, 200) : null,
    confirmedBy: null, confirmedAt: null, notEnforced: null, notes: [], ...extra.more };
}

// GENERATE. housePolicy: firms.compliance_config.housePolicy (fields, sources,
// uncertainties, frameworkConflicts, docPath, distilledAt). firm: the firm row
// (risk_config, compliance_config, features, second_signoff_*).
// v1_824: the entity types a varying ownership threshold is recorded for.
export const THRESHOLD_TYPES = Object.freeze(['private_company', 'regulated_firm', 'partnership', 'trust']);

// v1_824: a working draft shows the tenant's CURRENT wallet screening and
// Travel Rule settings until the MLRO confirms the field. A draft generated
// before the setting changed kept the old value (Sage's tenant, first use).
export function withCurrentFeatures(answers, features) {
  const a = answers && typeof answers === 'object' ? answers : {};
  const e = a.crypto;
  if (!e || e.confirmedAt) return a;
  const ft = features && typeof features === 'object' ? features : {};
  const v = e.value || {};
  const { inScope: _dropped, ...rest } = v;
  const value = { ...rest, walletScreening: !!ft.chainalysis, travelRule: !!ft.travel_rule };
  return { ...a, crypto: { ...e, value, source: 'mlro' } };
}

export function generateAnswers({ housePolicy = null, firm = {} } = {}) {
  const hp = housePolicy && typeof housePolicy === 'object' ? housePolicy : {};
  const hf = hp.fields && typeof hp.fields === 'object' ? hp.fields : {};
  const src = hp.sources && typeof hp.sources === 'object' ? hp.sources : {};  // distiller clause and quote per distilled field
  const rc = firm.risk_config && typeof firm.risk_config === 'object' ? firm.risk_config : {};
  const cc = firm.compliance_config && typeof firm.compliance_config === 'object' ? firm.compliance_config : {};
  const ft = firm.features && typeof firm.features === 'object' ? firm.features : {};
  const cfgAt = rc.firmConfigVersion || null;
  const mlro = (value, at) => entry(value, 'mlro', { more: { configuredAt: at || cfgAt } });
  const manual = (value, key) => entry(value, 'manual', { clause: (src[key] && src[key].clause) || null, quote: (src[key] && src[key].quote) || null });
  const def = (id) => entry(defaultValue(id), 'default');
  const a = {};

  // 1. Client types: the manual's acceptance policy is prose; the default
  // stands unless the MLRO sets it. The prose is attached as a note.
  a.clientTypes = def('clientTypes');
  if (str(hf.acceptancePolicy)) a.clientTypes.notes.push({ kind: 'manual_text', text: clip(hf.acceptancePolicy, 400), clause: (src.acceptancePolicy && src.acceptancePolicy.clause) || null });

  // 2. Jurisdictions: the firm's own country policy page wins.
  const cp = rc.countryPolicy && typeof rc.countryPolicy === 'object' ? rc.countryPolicy : null;
  a.jurisdictions = (cp && (list(cp.prohibited).length || list(cp.high).length))
    ? mlro({ prohibited: list(cp.prohibited), high: list(cp.high) })
    : def('jurisdictions');

  // 3. Ownership threshold: the manual may state one (enforced from v1_830).
  const thr = /(\d{1,2})\s*(%|percent)/i.exec([str(hf.thresholds), str(hf.notes)].join(' ').match(/beneficial[^.]*?\d{1,2}\s*(%|percent)/i)?.[0] || '');
  a.uboThreshold = thr ? manual({ percent: Number(thr[1]), variesByType: false }, 'thresholds') : def('uboThreshold');

  // 4. Mandatory documents: the manual's lists by type, mapped to the five
  // questionnaire types; a type the manual leaves open keeps the starter list,
  // marked default (a proposal).
  // v1_818: a manual's list for ONE board type ("MSB", "Virtual asset service
  // providers") is that type's own list, kept apart from the broad list, so
  // signing never spreads it to every regulated firm. A list for a group
  // ("Regulated firms", "Holding companies") is the broad list. A floor the
  // firm signed before is read back the same way: its broad lists and the
  // types it gave their own list.
  const distilled = hf.requiredDocsByEntityType && typeof hf.requiredDocsByEntityType === 'object' ? hf.requiredDocsByEntityType : {};
  const signedBefore = hf.requiredDocsSource === 'policy_questionnaire';
  const fromManual = {};
  const specific = {};
  const unplaced = [];
  const manualAllClients = [];
  if (signedBefore) {
    const broad = hf.requiredDocsBroad && typeof hf.requiredDocsBroad === 'object' ? hf.requiredDocsBroad : null;
    const sp = Array.isArray(hf.requiredDocsSpecificTypes) ? hf.requiredDocsSpecificTypes : [];
    for (const bt of sp) { const d = list(distilled[bt]); if (d.length && SPECIFIC_DOC_TYPES.includes(bt)) specific[bt] = d; }
    if (broad) { for (const qt of QUESTIONNAIRE_ENTITY_TYPES) { const d = list(broad[qt]); if (d.length) fromManual[qt] = d; } }
    else for (const [k, docs] of Object.entries(distilled)) {
      const d = list(docs); const qt = questionnaireTypeForKey(k);
      if (d.length && qt && !sp.includes(k)) fromManual[qt] = [...new Set([...(fromManual[qt] || []), ...d])];
    }
  } else for (const [k, docs] of Object.entries(distilled)) {
    const d = list(docs);
    if (!d.length) continue;
    // A firm wide list ("All clients") is section 14's documents, not a type.
    const nk = normalizeEntityTypeKey(k);
    if (nk === 'ALL') { manualAllClients.push(...d); continue; }
    if (SPECIFIC_DOC_TYPES.includes(nk) && nk !== 'OTHER') { specific[nk] = [...new Set([...(specific[nk] || []), ...d])]; continue; }
    const qt = questionnaireTypeForKey(k);
    if (!qt) { unplaced.push({ key: k, docs: d }); continue; }
    fromManual[qt] = [...new Set([...(fromManual[qt] || []), ...d])];
  }
  const md = {}; const mdSource = {};
  for (const qt of QUESTIONNAIRE_ENTITY_TYPES) {
    if (fromManual[qt]) { md[qt] = fromManual[qt]; mdSource[qt] = 'manual'; }
    else { md[qt] = [...STARTER_DOCS[qt]]; mdSource[qt] = 'default'; }
  }
  md.specific = specific;
  a.mandatoryDocs = entry(md, (Object.values(mdSource).includes('manual') || Object.keys(specific).length) ? 'manual' : 'default',
    { clause: (src.requiredDocsByEntityType && src.requiredDocsByEntityType.clause) || null, quote: (src.requiredDocsByEntityType && src.requiredDocsByEntityType.quote) || null,
      // The admin checklist's own lists, shown read only: those types keep them.
      more: { perType: mdSource, adminSpecific: adminSpecificByType(cc.documentChecklist) } });
  a.mandatoryDocs.notEnforced = unenforcedQuestionnaireTypes();

  // 5. Risk categorisation: bands and weights from the firm's config if set.
  const hasBands = rc.bands && (rc.bands.lowMax != null || rc.bands.highMin != null);
  const hasWeights = rc.weights && typeof rc.weights === 'object' && Object.keys(rc.weights).length;
  if (hasBands || hasWeights) {
    const d = defaultValue('riskCategorisation');
    a.riskCategorisation = mlro({ ...d, lowMax: hasBands && rc.bands.lowMax != null ? Number(rc.bands.lowMax) : d.lowMax, highMin: hasBands && rc.bands.highMin != null ? Number(rc.bands.highMin) : d.highMin, weights: hasWeights ? { ...d.weights, ...rc.weights } : d.weights });
  } else a.riskCategorisation = def('riskCategorisation');

  // 6. EDD triggers: the distilled list, recorded.

  // 7 and 9. Tier requirements from the firm's config if set.
  const tr = rc.tierRequirements && typeof rc.tierRequirements === 'object' ? rc.tierRequirements : null;
  if (tr && TIERS.some(t => tr[t] && tr[t].reviewYears != null)) {
    const d = defaultValue('kycRenewal');
    a.kycRenewal = mlro(Object.fromEntries(TIERS.map(t => [t, tr[t] && tr[t].reviewYears != null ? Number(tr[t].reviewYears) : d[t]])));
  } else a.kycRenewal = def('kycRenewal');
  if (tr && TIERS.some(t => tr[t] && tr[t].sowRequired != null)) {
    const d = defaultValue('sowInterview');
    a.sowInterview = mlro({ ...d, tiers: Object.fromEntries(TIERS.map(t => [t, tr[t] && tr[t].sowRequired != null ? !!tr[t].sowRequired : d.tiers[t]])) });
  } else a.sowInterview = def('sowInterview');

  // 8. Identity scope: platform minimum.
  a.idScope = def('idScope');

  // 10. Screening: second sign off from features.
  a.screening = def('screening');
  if (ft.direct_approval === false) a.screening = mlro({ ...defaultValue('screening'), secondSignoff: true });

  // 11. Roles: second signatory from the firm row, MLRO and deputy from the manual.
  const r = defaultValue('roles');
  let rolesSource = 'default';
  if (str(firm.second_signoff_name) || str(firm.second_signoff_email)) { r.secondSignatoryName = str(firm.second_signoff_name); r.secondSignatoryEmail = str(firm.second_signoff_email); rolesSource = 'mlro'; }
  const hm = hf.mlro && typeof hf.mlro === 'object' ? hf.mlro : {};
  if (str(hm.name) || str(hm.deputy)) { r.mlro = str(hm.name); r.deputy = str(hm.deputy); if (rolesSource === 'default') rolesSource = 'manual'; }
  a.roles = rolesSource === 'mlro' ? mlro(r) : rolesSource === 'manual' ? manual(r, 'mlro') : entry(r, 'default');

  // 12. Crypto: features.
  // v1_824: the tenant's current setting, always (a feature never set is off),
  // never the CompliantID default.
  a.crypto = mlro({ walletScreening: !!ft.chainalysis, travelRule: !!ft.travel_rule });

  // 13. Client terms.
  a.clientTerms = cc.clientTermsOutside === true ? mlro({ where: 'outside' }) : def('clientTerms');

  // 14. Every client: the checklist's common documents, and (v1_833) the
  // questions the firm already asks every client.
  const common = list(cc.documentChecklist && cc.documentChecklist.common);
  const firmQs = list(cc.firmQuestions);
  a.everyClient = (common.length || firmQs.length) ? mlro({ documents: common, questions: firmQs })
    : manualAllClients.length ? manual({ documents: [...new Set(manualAllClients)], questions: [] }, 'requiredDocsByEntityType')
    : def('everyClient');

  // The one fixed section (8), from FIELDS.
  for (const f of FIELDS) if (a[f.id] && f.enforced === 'fixed') a[f.id].notEnforced = a[f.id].notEnforced || true;

  // Distiller notes: each uncertainty and conflict to its field, else open points.
  const openPoints = [];
  for (const u of list(hp.uncertainties)) { const id = fieldForNote(u); if (id && a[id]) a[id].notes.push({ kind: 'question', text: u }); else openPoints.push({ kind: 'question', text: u }); }
  for (const c of list(hp.frameworkConflicts)) { const id = fieldForNote(c); if (id && a[id]) a[id].notes.push({ kind: 'conflict', text: c }); else openPoints.push({ kind: 'conflict', text: c }); }
  for (const u of unplaced) openPoints.push({ kind: 'unplaced_documents', text: `The manual lists documents for "${u.key}", which matches none of the five entity types: ${u.docs.join('; ')}` });

  return { answers: a, openPoints, generatedFrom: { docPath: hp.docPath || null, distilledAt: hp.distilledAt || null, configUpdatedAt: cfgAt, questionnaireVersion: QUESTIONNAIRE_VERSION } };
}

// VALIDATE one field's value against the engine's own limits. Returns
// { ok: true, value } (normalised) or { ok: false, reason }.
export function validateAnswer(id, value, { current } = {}) {
  const v = value && typeof value === 'object' ? value : {};
  switch (id) {
    case 'clientTypes': {
      const out = {};
      for (const t of CLIENT_TYPES) {
        const x = str(v[t.key]) || 'permitted';
        if (!['permitted', 'excluded', 'edd'].includes(x)) return { ok: false, reason: `${t.label}: choose permitted, excluded or EDD.` };
        if (t.fixed && x !== t.fixed && x !== 'excluded') return { ok: false, reason: `${t.label}: fixed by regulation at EDD. It may be excluded, never relaxed.` };
        out[t.key] = x;
      }
      return { ok: true, value: out };
    }
    case 'jurisdictions': return { ok: true, value: { prohibited: [...new Set(list(v.prohibited))], high: [...new Set(list(v.high))] } };
    case 'uboThreshold': {
      const p = Number(v.percent);
      if (!Number.isFinite(p) || p <= 0) return { ok: false, reason: 'Give the threshold as a percentage.' };
      if (p > 25) return { ok: false, reason: 'The ownership threshold may be lowered, never raised above 25 percent.' };
      // v1_824, v1_832: per type thresholds, applied by the legal form group,
      // then the entity type group. Each may be lowered, never raised above 25.
      const byType = {};
      if (v.variesByType) {
        for (const t of THRESHOLD_TYPES) {
          const raw = v.byType && v.byType[t];
          if (raw === undefined || raw === null || raw === '') continue;
          const n = Number(raw);
          if (!Number.isFinite(n) || n <= 0) return { ok: false, reason: `Give the threshold for ${QUESTIONNAIRE_ENTITY_LABELS[t]} as a percentage.` };
          if (n > 25) return { ok: false, reason: `The threshold for ${QUESTIONNAIRE_ENTITY_LABELS[t]} may be lowered, never raised above 25 percent.` };
          byType[t] = n;
        }
      }
      return { ok: true, value: { percent: p, variesByType: !!v.variesByType, ...(v.variesByType ? { byType } : {}) } };
    }
    case 'mandatoryDocs': {
      const out = {};
      for (const qt of QUESTIONNAIRE_ENTITY_TYPES) out[qt] = [...new Set(list(v[qt]))];
      // v1_818: a list for one board type. Only the case types the broad lists
      // apply to; an empty list is dropped (the type reads its broad list).
      const sp = v.specific && typeof v.specific === 'object' ? v.specific : {};
      const bad = Object.keys(sp).filter(k => !SPECIFIC_DOC_TYPES.includes(String(k).trim().toUpperCase()));
      if (bad.length) return { ok: false, reason: `A list for one type must name a case type from the table (not "${bad[0]}").` };
      out.specific = specificDocsOf({ specific: Object.fromEntries(Object.entries(sp).map(([k, d]) => [k, list(d)])) });
      return { ok: true, value: out };
    }
    case 'riskCategorisation': {
      const lowMax = Number(v.lowMax), highMin = Number(v.highMin);
      if (!(lowMax > 0) || lowMax > 1.75) return { ok: false, reason: 'The Low band may be lowered, not raised above 1.75.' };
      if (!(highMin > 0) || highMin > 2.5) return { ok: false, reason: 'The High band may be lowered, not raised above 2.50.' };
      if (!(lowMax < highMin)) return { ok: false, reason: 'The Low band must end below where the High band starts.' };
      const w = {}; let sum = 0;
      for (const c of CATEGORIES) { const n = Number((v.weights || {})[c]); if (!Number.isFinite(n)) return { ok: false, reason: `Give a weight for ${c}.` }; if (n < 5) return { ok: false, reason: 'No weight may be below 5.' }; w[c] = n; sum += n; }
      if (sum !== 100) return { ok: false, reason: `The weights must sum to 100 (they sum to ${sum}).` };
      return { ok: true, value: { lowMax, highMin, weights: w } };
    }
    case 'kycRenewal': {
      const out = {};
      for (const t of TIERS) {
        const n = Number(v[t]);
        if (!Number.isFinite(n) || n <= 0) return { ok: false, reason: `Give a review period in years for ${t} risk.` };
        if (n > TIER_REQUIREMENTS[t].reviewYears) return { ok: false, reason: `Review periods may be shortened, not lengthened, from the platform's minimum (${t} risk: every ${TIER_REQUIREMENTS[t].reviewYears} ${TIER_REQUIREMENTS[t].reviewYears === 1 ? 'year' : 'years'}).` };
        out[t] = n;
      }
      return { ok: true, value: out };
    }
    case 'idScope': {
      if (str(v.scope) && v.scope !== 'directors_ubos_signatories') return { ok: false, reason: 'Fixed by the platform: directors, beneficial owners and authorised signatories are always verified. A narrower scope would reduce verification.' };
      return { ok: true, value: { scope: 'directors_ubos_signatories' } };
    }
    case 'sowInterview': {
      const tiers = {};
      for (const t of TIERS) tiers[t] = !!(v.tiers || {})[t];
      if (!tiers.High) return { ok: false, reason: 'Fixed by regulation: High risk always requires source of wealth to be evidenced.' };
      // v1_830: no holding threshold of its own; section 3's applies.
      return { ok: true, value: { tiers } };
    }
    case 'screening': {
      if (str(v.pepTreatment) && v.pepTreatment !== 'high_edd') return { ok: false, reason: 'Fixed by regulation: a politically exposed person match always means High risk and EDD.' };
      return { ok: true, value: { pepTreatment: 'high_edd', approver: clip(v.approver, 200), secondSignoff: !!v.secondSignoff } };
    }
    case 'roles': {
      const e = str(v.secondSignatoryEmail);
      if (e && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) return { ok: false, reason: 'The second signatory email is not a valid address.' };
      return { ok: true, value: { mlro: clip(v.mlro, 200), deputy: clip(v.deputy, 200), secondSignatoryName: clip(v.secondSignatoryName, 200), secondSignatoryEmail: e, overrideApprover: clip(v.overrideApprover, 200) } };
    }
    case 'crypto': return { ok: true, value: { walletScreening: !!v.walletScreening, travelRule: !!v.travelRule } };
    case 'clientTerms': {
      if (!['platform', 'outside'].includes(v.where)) return { ok: false, reason: 'Choose in the platform, or outside it.' };
      return { ok: true, value: { where: v.where } };
    }
    case 'everyClient': return { ok: true, value: { documents: [...new Set(list(v.documents))], questions: [...new Set(list(v.questions))] } };
    default: return { ok: false, reason: 'Unknown field.' };
  }
}

// Cross field checks at sign time.
export function validateForSigning(answers) {
  const problems = [];
  const a = answers || {};
  for (const f of FIELDS) {
    const e = a[f.id];
    if (!e) { problems.push({ id: f.id, reason: 'Missing.' }); continue; }
    if (!e.confirmedAt) problems.push({ id: f.id, reason: 'Not yet confirmed.' });
    const r = validateAnswer(f.id, e.value);
    if (!r.ok) problems.push({ id: f.id, reason: r.reason });
  }
  const scr = a.screening && a.screening.value, roles = a.roles && a.roles.value;
  if (scr && scr.secondSignoff && roles && !(str(roles.secondSignatoryName) && str(roles.secondSignatoryEmail)))
    problems.push({ id: 'roles', reason: 'Second sign off is required, so name the second signatory and give their email.' });
  return problems;
}

export function progress(answers) {
  const a = answers || {};
  const confirmed = FIELDS.filter(f => a[f.id] && a[f.id].confirmedAt).length;
  // v1_836: every section is enforced, so the line counts confirmations only.
  return { confirmed, total: FIELDS.length, line: `${confirmed} of ${FIELDS.length} sections confirmed` };
}

// REBUILD the firm configuration from a signed questionnaire. Returns the
// patch for the firms row: { risk_config, compliance_config, features,
// second_signoff_name, second_signoff_email }, each merged over the current
// value. Only the keys in FIELDS' engineKey are written. Pure.
export function buildFirmPolicyConfig(signed, current = {}) {
  const a = (signed && signed.answers) || {};
  const val = (id) => (a[id] && a[id].value) || defaultValue(id);
  const rc0 = current.risk_config && typeof current.risk_config === 'object' ? current.risk_config : {};
  const cc0 = current.compliance_config && typeof current.compliance_config === 'object' ? current.compliance_config : {};
  const ft0 = current.features && typeof current.features === 'object' ? current.features : {};

  // risk_config
  const j = val('jurisdictions');
  const cp0 = rc0.countryPolicy && typeof rc0.countryPolicy === 'object' ? rc0.countryPolicy : {};
  const countryPolicy = { ...cp0, prohibited: list(j.prohibited), high: list(j.high), medium: list(cp0.medium) };
  const sectorRisk = { ...(rc0.sectorRisk || {}) };
  const ct = val('clientTypes');
  // v1_843: the questionnaire owns the sectors its client types map to (only
  // it writes sectorRisk). EDD pins them High; any other answer clears the
  // pin, so re-signing EDD as Permitted lets the evidence rate the sector
  // again (the v1_839 VASP mitigation). A spread with no clear left an
  // earlier EDD answer in force after the firm changed it.
  for (const [k, sectors] of Object.entries(CLIENT_TYPE_SECTOR)) for (const s of sectors) {
    if (ct[k] === 'edd') sectorRisk[s] = 'High'; else delete sectorRisk[s];
  }
  const rcat = val('riskCategorisation');
  const kyc = val('kycRenewal'), sow = val('sowInterview');
  const tr0 = rc0.tierRequirements && typeof rc0.tierRequirements === 'object' ? rc0.tierRequirements : {};
  // The engine replaces a whole tier object on override, so write full tiers.
  const tierRequirements = Object.fromEntries(TIERS.map(t => [t, { ...TIER_REQUIREMENTS[t], ...(tr0[t] || {}), reviewYears: Number(kyc[t]), sowRequired: !!(sow.tiers || {})[t] }]));
  const version = signed && signed.version;
  const signedAt = (signed && signed.signed_at) || null;
  // v1_830: section 3's threshold, read by the engine at each rebuild and retier.
  const uboThreshold = Number(val('uboThreshold').percent);
  // v1_832: the per type values, applied by the legal form group, then the
  // entity type group (only when the firm said the threshold varies by type).
  const ubt = val('uboThreshold');
  const uboThresholdByType = ubt.variesByType ? Object.fromEntries(THRESHOLD_TYPES.map(t => [t, Number((ubt.byType || {})[t])]).filter(([, n]) => n > 0 && n <= DEFAULT_OWNERSHIP_THRESHOLD)) : {};
  // v1_831: the excluded client types (all six recorded; the engine acts on
  // the types it detects).
  const excludedClientTypes = CLIENT_TYPES.filter(t => ct[t.key] === 'excluded').map(t => t.key);
  // v1_848: the EDD answers for the four types with no sector key. Written in
  // full on every signing, so a type re-signed as Permitted drops out.
  const eddClientTypes = CLIENT_TYPES.filter(t => ct[t.key] === 'edd' && EDD_FLOOR_CLIENT_TYPES.includes(t.key)).map(t => t.key);
  // v1_834w: a riskFactors text an earlier (v1_834) signing wrote is dropped.
  const { riskFactors: _withdrawnFactors, ...rc0Kept } = rc0 || {};
  const risk_config = {
    ...rc0Kept, countryPolicy, sectorRisk, excludedClientTypes, eddClientTypes,
    uboThreshold: uboThreshold > 0 && uboThreshold <= DEFAULT_OWNERSHIP_THRESHOLD ? uboThreshold : DEFAULT_OWNERSHIP_THRESHOLD,
    uboThresholdByType,
    bands: { ...(rc0.bands || {}), lowMax: Number(rcat.lowMax), highMin: Number(rcat.highMin) },
    weights: { ...rcat.weights },
    tierRequirements,
    policyBasis: { questionnaireId: (signed && signed.id) || null, version: version || null, signedBy: (signed && signed.signed_by_name) || null, signedAt },
    firmConfigVersion: `PQ v${version || 0} ${String(signedAt || '').slice(0, 10)}`.trim(),
  };

  // compliance_config
  const md = val('mandatoryDocs');
  const floor = floorFromQuestionnaireDocs(md);
  const hp0 = cc0.housePolicy && typeof cc0.housePolicy === 'object' ? cc0.housePolicy : {};
  const hf0 = hp0.fields && typeof hp0.fields === 'object' ? hp0.fields : {};
  const ec = val('everyClient');
  const dc0 = cc0.documentChecklist && typeof cc0.documentChecklist === 'object' ? cc0.documentChecklist : {};
  const compliance_config = {
    ...cc0,
    // v1_818: the broad lists and the types given their own list are stored
    // apart, so the floor applies a broad list only to a type with no list of
    // its own (the admin checklist's, or one set here).
    housePolicy: { ...hp0, fields: { ...hf0, requiredDocsByEntityType: floor, requiredDocsByEntityTypeNormalized: normalizeRequiredDocsByEntityType(floor), requiredDocsSource: 'policy_questionnaire',
      requiredDocsBroad: Object.fromEntries(QUESTIONNAIRE_ENTITY_TYPES.map(qt => [qt, list((md || {})[qt])])),
      requiredDocsSpecificTypes: Object.keys(specificDocsOf(md)) } },
    documentChecklist: { ...dc0, common: list(ec.documents) },
    // v1_833: asked of every client (interview, documents first checklist, return visit).
    firmQuestions: list(ec.questions),
    // v1_835: the named officers (sections 10 and 11), shown at sign off and recorded on the decision.
    policyOfficers: policyOfficersOf({ approver: val('screening').approver, mlro: val('roles').mlro, deputy: val('roles').deputy, overrideApprover: val('roles').overrideApprover }),
    clientTermsOutside: val('clientTerms').where === 'outside',
  };

  // features: only the three keys the questionnaire maps.
  const cr = val('crypto'), scr = val('screening');
  const features = { ...ft0, chainalysis: !!cr.walletScreening, travel_rule: !!cr.travelRule, direct_approval: !scr.secondSignoff };

  const roles = val('roles');
  return {
    risk_config, compliance_config, features,
    second_signoff_name: str(roles.secondSignatoryName) || null,
    second_signoff_email: str(roles.secondSignatoryEmail) || null,
  };
}

// The Audit Record's "Policy basis" rows for a case (plan section 5).
export function policyBasisRows({ questionnaire = null, firm = {}, entityType = null, legalForm = null } = {}) {
  const signed = questionnaire && questionnaire.status === 'signed' ? questionnaire : null;
  const a = (signed && signed.answers) || {};
  const who = signed ? `${signed.signed_by_name || 'the MLRO'} on ${String(signed.signed_at || '').slice(0, 10)}` : null;
  const srcLabel = (id) => {
    const e = a[id];
    const f = FIELD_BY_ID[id];
    if (!signed) return 'Platform standard, not set by the firm';
    if (f.enforced === 'fixed') return 'Platform standard, fixed';
    if (!e) return 'Platform standard, not set by the firm';
    if (e.source === 'manual') return `Manual${e.clause ? ' clause ' + e.clause : ''}, signed by ${who}`;
    if (e.source === 'default') return `Confirmed default, signed by ${who}`;
    if (e.source === 'platform_owner_pdf') return `Entered from the firm's signed PDF, signed by ${who}`;
    return `Set by the firm, signed by ${who}`;
  };
  // v1_832: the legal form's group (partnership, trust, individual) comes
  // before the entity type's, as in the floor itself.
  const valOf = (id) => (a[id] && a[id].value) || {};
  const typeList = (treatment) => {
    const ct = valOf('clientTypes');
    const names = CLIENT_TYPES.filter(t => ct[t.key] === treatment).map(t => t.label.charAt(0).toLowerCase() + t.label.slice(1));
    return names.length ? names.join(', ') : 'none';
  };
  const everyClientCount = () => {
    const v = valOf('everyClient');
    const d = (v.documents || []).length, q = (v.questions || []).length;
    return `${d} ${d === 1 ? 'document' : 'documents'}, ${q} ${q === 1 ? 'question' : 'questions'}`;
  };
  const lg = legalForm && legalForm.group;
  const qt = lg || questionnaireTypeForBoardType(entityType);
  // v1_818: which list the case's type reads: its own list set in the
  // questionnaire, the admin checklist's own list, or its type's broad list.
  const bt = floorBoardTypeFor(entityType);
  const ownQ = signed && bt && specificDocsOf((a.mandatoryDocs || {}).value)[bt];
  const ownAdmin = signed && bt && adminSpecificDocs(((firm && firm.compliance_config) || {}).documentChecklist, normalizeEntityTypeKey(entityType)).length;
  const mdScope = ownQ ? `${boardTypeLabel(bt)}, its own list` : ownAdmin ? `${boardTypeLabel(bt)}, the admin checklist's own list` : (qt ? QUESTIONNAIRE_ENTITY_LABELS[qt] + (lg ? `, by the legal form (${String(legalForm.label || legalForm.form).toLowerCase()})` : '') : '');
  const mdSource = ownQ ? srcLabel('mandatoryDocs') : ownAdmin ? 'Admin document checklist, kept in place of the broad list' : (qt ? srcLabel('mandatoryDocs') : (signed ? 'No list applies to this entity type' : 'Platform standard, not set by the firm'));
  return [
    { rule: 'Jurisdiction lists', source: srcLabel('jurisdictions') },
    { rule: 'Risk bands and weights', source: srcLabel('riskCategorisation') },
    // v1_834w: the written factors are not collected; the row says so.
    { rule: 'Written factors', source: 'not collected; the firm\'s manual is the source' },
    { rule: 'Sector overrides', source: srcLabel('clientTypes') },
    // v1_848: the section 1 answers that act on a case, named.
    { rule: `Excluded client types${signed ? ' (' + typeList('excluded') + ')' : ''}`, source: srcLabel('clientTypes') },
    { rule: `Client types treated as enhanced due diligence${signed ? ' (' + typeList('edd') + ')' : ''}`, source: srcLabel('clientTypes') },
    { rule: `Mandatory document floor${mdScope ? ' (' + mdScope + ')' : ''}`, source: mdSource },
    // v1_830: enforced; the row states the threshold in words.
    { rule: 'Ownership threshold', source: signed
      ? `${thresholdStatement(Number(((a.uboThreshold || {}).value || {}).percent) || DEFAULT_OWNERSHIP_THRESHOLD)}; ${srcLabel('uboThreshold')}`
      : 'Platform standard, not set by the firm' },
    { rule: 'Review period', source: srcLabel('kycRenewal') },
    { rule: 'Identity verification scope', source: 'Platform standard, fixed' },
    { rule: 'Source of wealth interview', source: srcLabel('sowInterview') },
    // v1_848: second sign off (section 10), wallet screening and the Travel
    // Rule (section 12), and what is asked of every client (section 14).
    { rule: `Second sign off${signed ? (valOf('screening').secondSignoff ? ' (required)' : ' (not required)') : ''}`, source: srcLabel('screening') },
    { rule: `Wallet screening and the Travel Rule${signed ? ' (wallet screening ' + (valOf('crypto').walletScreening ? 'on' : 'off') + ', Travel Rule ' + (valOf('crypto').travelRule ? 'on' : 'off') + ')' : ''}`, source: srcLabel('crypto') },
    { rule: `Asked of every client${signed ? ' (' + everyClientCount() + ')' : ''}`, source: srcLabel('everyClient') },
  ];
}

// THE CASE'S POLICY BASIS at a rebuild (v1_814), stamped on the board by every
// generation path (documents first and Reprocess in df-process; interview submit
// and officer regenerate through the retier), from the firm's risk_config read
// fresh by that path. Signing never reprocesses a case: the next rebuild picks
// the new basis up, and writes one case note when it changed.
export function policyBasisFor(firmRiskConfig) {
  const pb = firmRiskConfig && firmRiskConfig.policyBasis;
  return pb && pb.version ? { questionnaireId: pb.questionnaireId || null, version: Number(pb.version), signedBy: pb.signedBy || null, signedAt: pb.signedAt || null } : null;
}
export function policyBasisChangeNote(prior, next) {
  const pv = prior && prior.version ? Number(prior.version) : null;
  const nv = next && next.version ? Number(next.version) : null;
  if (pv === nv) return null;
  if (!nv) return null; // the firm's basis cleared: nothing new applies, no note
  return `The policy basis changed: policy questionnaire version ${nv}, signed by ${next.signedBy || 'the MLRO'} on ${String(next.signedAt || '').slice(0, 10)}, applies from this rebuild.`;
}
